import type { AxiosInstance } from 'axios'

/**
 * Creates a response interceptor that emits events when trip dashboard data changes
 * Works with both trip-dashboard-axios and api-axios
 * @param axiosInstance - The axios instance to add the interceptor to
 */
export const createTripDashboardAxiosChangeInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.response.use(
    response => {
      const url = response.config.url || ''
      const method = response.config.method?.toLowerCase() || ''

      // Extract trip dashboard ID from URL
      // Pattern 1: /trip-dashboard/{dashboardId}/... (from trip-dashboard-axios)
      // Pattern 2: /api/v1/trip-dashboard/{dashboardId}/... (from api-axios)
      // Pattern 3: v1/trip-dashboard/{dashboardId}/... (relative to /api/)
      // Pattern 4: {dashboardId}/... (relative to /trip-dashboard/)
      const urlMatch = url.match(/\/trip-dashboard\/(\d+)\//)
        || url.match(/\/api\/v1\/trip-dashboard\/(\d+)\//)
        || url.match(/v1\/trip-dashboard\/(\d+)\//)
        || url.match(/^(\d+)\//)

      if (urlMatch && urlMatch[1]) {
        const tripDashboardId = parseInt(urlMatch[1], 10)

        // Only proceed if we have a valid positive number
        if (!isNaN(tripDashboardId) && tripDashboardId > 0) {
          // @ts-ignore - window.tripDashboardEventBus is set up in eventBus.ts
          if (window.tripDashboardEventBus) {
            // Check if this is a user-related operation (for specific event)
            const userOperationPatterns = [
              /\/addUser$/,
              /\/unlinkUser$/,
              /\/createUser$/,
              /\/setUserPermission$/,
              /\/addNonCustomerUser$/,
              /\/unlinkNonCustomerUser$/
            ]
            const isUserOperation = userOperationPatterns.some(pattern => pattern.test(url))

            // Emit specific event for user operations
            if (isUserOperation && method === 'post' && response.status === 200) {
              // @ts-ignore
              window.tripDashboardEventBus.emit('trip-dashboard:users-changed', {
                tripDashboardId
              })
            }

            // Emit generic event for any mutation (POST, PUT, DELETE)
            if (
              (method === 'post' || method === 'put' || method === 'delete')
              && response.status >= 200
              && response.status < 300
            ) {
              // @ts-ignore
              window.tripDashboardEventBus.emit('trip-dashboard:something-changed', {
                tripDashboardId
              })
            }
          }
        }
      }

      return response
    },
    error => {
      return Promise.reject(error)
    }
  )
}
